<template>
  <div class="RePersoanlDetail" data-scroll="personalDetail">
    <div class="PersoanlDetail_header flex-between">
      <h2 class="title">{{ $t('checkout.personalDetails.title', 'Personal details') }}</h2>
      <app-edit-button
        v-if="!hasSubscription"
        :editable="editable"
        @click.native="editPersonalDetail"
        data-name="personalDetail-editButton"
        :disabled="updatePersonalDetailTask.isActive"
      />
    </div>

    <div class="formContent" v-show="isShow">
      <div class="privacyDeclaration" v-html="$t('checkout.privacyDeclaration.text', '')"></div>
      <form class="PersoanlDetail_body" autocomplete="on">
        <div class="content">
          <r-app-text-input
            :label="$t('checkout.personalDetails.placeholder.firstName', 'First name')"
            :placeholder="$t('checkout.personalDetails.placeholder.firstName', 'First name')"
            v-model.trim="firstName"
            :errors="errorsFor('firstName')"
            required="required"
            autofocus="autofocus"
            autocomplete="given-name"
            name="signUp-givenName"
            :disabled="updatePersonalDetailTask.isActive"
            :top-label="topLabel"
          />
          <r-app-text-input
            :label="$t('checkout.personalDetails.placeholder.lastName', 'Last name')"
            :placeholder="$t('checkout.personalDetails.placeholder.lastName', 'Last name')"
            v-model.trim="lastName"
            :errors="errorsFor('lastName')"
            :disabled="updatePersonalDetailTask.isActive"
            autocomplete="family-name"
            name="signUp-familyName"
            data-name="editPersonalDetails-lastName"
            required="required"
            :top-label="topLabel"
          />
        </div>
        <app-phone-number-input
          v-if="phoneNumberVisible"
          :label="$t('checkout.personalDetails.placeholder.phoneNumber', 'Phone number')"
          :placeholder="$t('checkout.personalDetails.placeholder.phoneNumber', 'Phone number')"
          v-model.trim="phoneNumber"
          :errors="errorsFor('phoneNumber')"
          :disabled="updatePersonalDetailTask.isActive"
          autocomplete="tel"
          name="signUp-tel"
          data-name="editPersonalDetails-phoneNumber"
          type="tel"
          required="required"
          :top-label="topLabel"
        />
        <!-- <extra-optin-note :content="$t('common.personalDetail.newsletterNote', '')" />
        <app-checkbox
          id="newsletter"
          :label="$t('common.personalDetail.newsletter', 'Philips may send me personalized news and offers. You can log out easily and at any time.')"
          :value="selectedNewsletter"
          :disabled="updatePersonalDetailTask.isActive"
          @input="selectedNewsletter = $event"
          @click="trackConversion({name: 'interaction', description: `email_promotion:${selectedNewsletter ? 'uncheck': 'check'}`})"
        >
          <a
            class="link"
            target="_blank"
            :href="$t('common.personalDetail.newsletter.linkUrl', '##')">{{ $t('common.personalDetail.newsletter.linkText', 'What does this mean?') }}
          </a>
        </app-checkbox> -->
        <app-button
          type="submit"
          @click.prevent="updatePersonalDetailTask.run()"
          :is-loading="updatePersonalDetailTask.isActive"
          data-name="checkoutPersonalDetail-updateButton"
        >
          {{ $t('checkout.button.next', 'Next') }}
        </app-button>
      </form>
    </div>

    <div class="formContent" v-show="!isShow && hasSubscription">
      <div class="privacyDeclaration" v-html="$t('checkout.privacyDeclaration.text', '')"></div>
      <form class="PersoanlDetail_body">
        <div class="content">
          <r-app-text-input
            :label="$t('checkout.personalDetails.placeholder.firstName', 'First name')"
            :placeholder="$t('checkout.personalDetails.placeholder.firstName', 'First name')"
            v-model.trim="firstName"
            disabled
            :top-label="topLabel"
          />
          <r-app-text-input
            :label="$t('checkout.personalDetails.placeholder.lastName', 'Last name')"
            :placeholder="$t('checkout.personalDetails.placeholder.lastName', 'Last name')"
            v-model.trim="lastName"
            disabled
            :top-label="topLabel"
          />
        </div>
        <app-phone-number-input
          v-if="phoneNumberVisible"
          :label="$t('checkout.personalDetails.placeholder.phoneNumber', 'Phone number')"
          :placeholder="$t('checkout.personalDetails.placeholder.phoneNumber', 'Phone number')"
          v-model.trim="phoneNumber"
          disabled
          :top-label="topLabel"
        />
        <r-app-text-input
          :label="$t('checkout.personalDetails.textLable.email', 'E-mail')"
          :placeholder="$t('checkout.personalDetails.textLable.email', 'E-mail')"
          v-model.trim="personalDetail.email"
          disabled
          :top-label="topLabel"
        />
      </form>
    </div>
  </div>
</template>

<script>
  import PersonalDetailMixin from 'mixins/personal-detail'
  import { required, maxLength, requiredIf } from 'vuelidate/lib/validators'
  import { name, phoneNumberRegex } from '@/utilities/common/validators'
  import { detectedCulture } from 'utilities/common/culture'
  import { mapState, mapGetters } from 'vuex'
  import { remove, startsWith } from 'lodash'
  import ExtraOptinNote from 'components/ExtraOptinNote'

  export default {
    name: 'checkout-personal-detail',

    components: {
      ExtraOptinNote
    },

    mixins: [PersonalDetailMixin],

    props: {
      hasSubscription: {
        type: Boolean,
        required: true
      },

      bundles: {
        type: Array,
        required: true
      }
    },

    data () {
      return {
        countryCode: detectedCulture.countryCode,
        topLabel: true
      }
    },

    computed: {
      fullName () {
        return this.personalDetail.firstName + ' ' + this.personalDetail.lastName
      },

      ...mapState('checkout', {
        phoneNumberVisible: state => state.cartDetails.phoneNumberVisible
      }),

      ...mapGetters('checkout', ['bundlePhoneNumberisRequired'])
    },

    validations: {
      firstName: {
        required,
        name,
        maxLength: maxLength(14)
      },

      lastName: {
        required,
        name,
        maxLength: maxLength(20)
      },

      email: {
        required
      },

      phoneNumber: {
        required: requiredIf(function () {
          return this.bundlePhoneNumberisRequired
        }),

        tel (value) {
          if (!value) return true
          if (this.countryCode === 'GB') {
            let valueSpace = value.match(/\s/gi)
            if (valueSpace && valueSpace.length > 2) {
              return false
            } else {
              return phoneNumberRegex.test(value)
            }
          } else {
            return phoneNumberRegex.test(value)
          }
        }
      }
    },

    mounted () {
      if (this.bundlePhoneNumberisRequired && !this.personalDetail.initialPhoneNumber) {
        this.editable = true
        this.showValidations = true
        this.isShow = true
      }

      const editableButton = document.getElementsByClassName('AppEditButton')
      if (editableButton.length > 0 && this.$v.$invalid) {
        this.editable = true
        this.showValidations = true
        this.isShow = true
      }
    },

    methods: {
      trackErrorFormFields () {
        let description = ''
        const formFields = remove(Object.keys(this.$v), item => !startsWith(item, '$'))
        formFields.forEach((field, index) => {
          if (this.$v[field].$invalid) {
            description = `${description}${field.replace(/([A-Z])/g, '_$1').toLowerCase()}|`
          }
        })
        this.trackConversion({ name: 'validation_error', description: description.slice(0, -1) })
      },

      additionalActions () {
        this.$store.commit('account/setInitialPhoneNumber', this.personalDetail.phoneNumber)
        if (this.hasSubscription) {
          this.$bus.$emit('scroll-next-step', '[data-scroll="paymentMethod"]')
        } else {
          this.$bus.$emit('scroll-next-step', '[data-scroll="address"]')
        }
      }
    }
  }
</script>

<style lang="scss">
  .RePersoanlDetail {
    border-bottom: .01rem solid #d6d6d6;
    padding: .4rem 0 .175rem;

    .PersoanlDetail_body {
      .content {
        @include media(tablet) {
          display: flex;
          justify-content: space-between;
        }

        .Re_form_group:nth-of-type(1) {
          @include media(tablet) {
            width: 40%;
            margin-right: .21rem;
          }
        }

        .Re_form_group:nth-of-type(2) {
          @include media(tablet) {
            width: 58%;
          }
        }
      }

      .AppButton {
        @include media(tablet) {
          width: auto !important;
          padding: .09rem .81rem;
        }
      }
    }

    .PersoanlDetail_header {
      .title {
        margin-left: .12rem;
      }
    }

    .privacyDeclaration {
      padding: 0 .1rem .1rem;
    }
  }
</style>
